import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

type Branding = {
  logo_url: string | null;
  primary_color: string | null;
  accent_color: string | null;
  subdomain: string | null;
};

const BrandingContext = createContext<Branding>({
  logo_url: null, primary_color: null, accent_color: null, subdomain: null,
});

// Lazy-engagement: like AuthProvider, BrandingProvider does nothing (and
// crucially does NOT call useAuth()) until a component actually reads the
// branding context via useBranding(). Marketing routes skip the whole thing.
let engagedGlobally = false;
const listeners = new Set<() => void>();

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [engaged, setEngaged] = useState(engagedGlobally);
  const [branding, setBranding] = useState<Branding>({
    logo_url: null, primary_color: null, accent_color: null, subdomain: null,
  });

  useEffect(() => {
    if (engagedGlobally) { setEngaged(true); return; }
    const l = () => setEngaged(true);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (branding.primary_color) root.style.setProperty("--primary", branding.primary_color);
    if (branding.accent_color) root.style.setProperty("--accent", branding.accent_color);
    return () => {
      root.style.removeProperty("--primary");
      root.style.removeProperty("--accent");
    };
  }, [branding.primary_color, branding.accent_color]);

  // Children are ALWAYS wrapped in a stable BrandingContext.Provider so the
  // fiber tree doesn't change type when engagement flips (which would remount
  // the entire authenticated subtree — flicker, restarted requests, lost input).
  // The loader that reads useAuth() is a sibling mounted only once engaged, so
  // marketing routes never engage AuthProvider.
  return (
    <BrandingContext.Provider value={branding}>
      {engaged && <BrandingLoader onChange={setBranding} />}
      {children}
    </BrandingContext.Provider>
  );
}


function BrandingLoader({ onChange }: { onChange: (b: Branding) => void }) {
  const { currentOrg } = useAuth();

  useEffect(() => {
    if (!currentOrg) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("org_branding")
        .select("logo_url, primary_color, accent_color, subdomain")
        .eq("organization_id", currentOrg.id)
        .maybeSingle();
      if (cancelled || !data) return;
      let logo_url = data.logo_url;
      if (logo_url && !logo_url.startsWith("http")) {
        const { data: signed } = await supabase.storage
          .from("org-branding")
          .createSignedUrl(logo_url, 3600);
        logo_url = signed?.signedUrl ?? null;
      }
      if (!cancelled) onChange({ ...data, logo_url } as Branding);
    })();
    return () => { cancelled = true; };
  }, [currentOrg?.id, onChange]);

  return null;
}

export function useBranding() {
  useEffect(() => {
    if (engagedGlobally) return;
    engagedGlobally = true;
    listeners.forEach((l) => l());
  }, []);
  return useContext(BrandingContext);
}

